import { supabase } from '@/integrations/supabase/client';
import { HabitTask } from '@/types';
import { handleDatabaseError, handleAuthError } from '@/lib/errorHandler';
import { createHabitSchema } from '@/lib/validations';
import { normalizePillarArray, normalizePillarName } from '@/lib/pillarMapping';

export const habitsService = {
  async createHabit(habit: Omit<HabitTask, 'id' | 'history'>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        handleAuthError(new Error('Usuário não autenticado'));
        throw new Error('Usuário não autenticado');
      }

      // Validate input
      createHabitSchema.parse({
        title: habit.title,
        frequency: habit.frequency,
        daysOfWeek: habit.daysOfWeek
      });

      // ✅ Normalizar pilares antes de inserir no banco
      const normalizedPillars = normalizePillarArray(habit.pillars || [habit.pillar]);
      const normalizedPillar = normalizePillarName(habit.pillar);
      
      const { data, error } = await supabase
        .from('user_habits')
        .insert({
          user_id: user.id,
          title: habit.title,
          pilar: normalizedPillar,
          pillars: normalizedPillars,
          frequency: habit.frequency === 'daily' ? 'diaria' : habit.frequency === 'weekly' ? 'semanal' : 'mensal',
          days_of_week: habit.daysOfWeek?.map(String) || [],
          specific_dates: [],
          active: true,
          // Phase 1: Fogg Model fields
          motivation_why: habit.motivationWhy,
          identity_vote_target: habit.identityVoteTarget,
          motivation_level: habit.motivationLevel || 5,
          capability_level: habit.capabilityLevel || 5,
          prompt_type: habit.promptType || 'notification',
          prompt_timing: habit.promptTiming,
          auto_adjust_enabled: habit.autoAdjustEnabled ?? true,
          micro_version: habit.microVersion,
          rest_days: habit.restDays || []
        })
        .select()
        .single();

      if (error) {
        handleDatabaseError(error);
        throw error;
      }
      return data.id;
    } catch (error) {
      if (error instanceof Error && error.message.includes('autenticado')) {
        throw error;
      }
      handleDatabaseError(error);
      throw error;
    }
  },

  async updateHabit(habitId: string, updates: Partial<HabitTask>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    // ⚡ VALIDAR updates
    if (updates.title && updates.title.trim().length < 3) {
      throw new Error('Título deve ter no mínimo 3 caracteres');
    }
    
    if (updates.frequency && !['daily', 'weekly', 'monthly'].includes(updates.frequency)) {
      throw new Error('Frequência inválida');
    }

    const updateData: any = {};
    
    // Campos básicos
    if (updates.title) updateData.title = updates.title.trim();
    if (updates.description !== undefined) updateData.habit_description = updates.description;
    if (updates.hour !== undefined) updateData.hour = updates.hour;
    if (updates.duration !== undefined) updateData.duration = updates.duration;
    
    // Frequência
    if (updates.frequency) updateData.frequency = updates.frequency === 'daily' ? 'diaria' : updates.frequency === 'weekly' ? 'semanal' : 'mensal';
    if (updates.daysOfWeek) updateData.days_of_week = updates.daysOfWeek.map(String);
    if (updates.dayOfMonth !== undefined) updateData.day_of_month = updates.dayOfMonth;
    
    // ✅ Sistema de múltiplos pilares com normalização
    if (updates.pillars) {
      const normalizedPillars = normalizePillarArray(updates.pillars);
      updateData.pillars = normalizedPillars;
      // Mantém compatibilidade com campo antigo
      updateData.pilar = normalizePillarName(updates.pillar || normalizedPillars[0]);
      
      console.log('🔄 Pilares normalizados:', { original: updates.pillars, normalized: normalizedPillars });
    }
    
    // Modelo Fogg
    if (updates.motivationWhy !== undefined) updateData.motivation_why = updates.motivationWhy;
    if (updates.identityVoteTarget !== undefined) updateData.identity_vote_target = updates.identityVoteTarget;
    if (updates.motivationLevel !== undefined) updateData.motivation_level = updates.motivationLevel;
    if (updates.capabilityLevel !== undefined) updateData.capability_level = updates.capabilityLevel;
    if (updates.promptType !== undefined) updateData.prompt_type = updates.promptType;
    if (updates.promptTiming !== undefined) updateData.prompt_timing = updates.promptTiming;
    if (updates.autoAdjustEnabled !== undefined) updateData.auto_adjust_enabled = updates.autoAdjustEnabled;
    if (updates.microVersion !== undefined) updateData.micro_version = updates.microVersion;
    if (updates.restDays !== undefined) updateData.rest_days = updates.restDays;

    const { error } = await supabase
      .from('user_habits')
      .update(updateData)
      .eq('id', habitId)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  async deleteHabit(habitId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    // 1. Marcar como inativo
    const { error: habitError } = await supabase
      .from('user_habits')
      .update({ active: false })
      .eq('id', habitId)
      .eq('user_id', user.id);

    if (habitError) throw habitError;

    // 2. Limpar dados relacionados (evitar erros 406 e race conditions)
    try {
      // Remover check-ins órfãos
      await supabase
        .from('habit_checkins')
        .delete()
        .eq('habit_id', habitId);

      // Remover performance tracking
      await supabase
        .from('habit_performance')
        .delete()
        .eq('habit_id', habitId)
        .eq('user_id', user.id);

      // Remover moving averages
      await supabase
        .from('habit_moving_averages')
        .delete()
        .eq('habit_id', habitId);
    } catch (cleanupError) {
      // Erros de limpeza não devem falhar a operação principal
      console.warn('⚠️ Erro ao limpar dados relacionados:', cleanupError);
    }
  },

  async toggleHabitDate(habitId: string, dateISO: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    // Verificar se já existe check-in
    const { data: existing } = await supabase
      .from('habit_checkins')
      .select('*')
      .eq('habit_id', habitId)
      .eq('date', dateISO)
      .maybeSingle();

    if (existing) {
      // Remover check-in
      const { error } = await supabase
        .from('habit_checkins')
        .delete()
        .eq('id', existing.id);

      if (error) throw error;
    } else {
      // Adicionar check-in
      const { error } = await supabase
        .from('habit_checkins')
        .insert({
          habit_id: habitId,
          user_id: user.id,
          date: dateISO,
          completed: true
        });

      if (error) throw error;
    }
  }
};
